import { useEffect, useState } from "react";
import { applyBranding, type RoasteryLook } from "../lib/branding";

/**
 * The roastery's logo, or its name when there is no logo (US-10.5).
 *
 * <p>A white-label portal has to say whose shop it is in the one place a customer looks first. The
 * name is the fallback rather than the exception: most small roasteries will never upload anything,
 * and a portal that says "Cafe Rynek's roastery" in plain type is white-labelled enough.
 *
 * <p>The image is bounded in height and never in aspect: a roastery pasting a 2000-pixel banner gets
 * it scaled, not a broken header. A URL that does not load falls back to the name, because a broken
 * image icon is worse branding than no image at all.
 */
export function RoasteryMark({
  name,
  logoUrl,
  className,
  testId,
}: {
  name?: string;
  logoUrl?: string | null;
  className?: string;
  testId?: string;
}) {
  // Reset the failure when the roastery changes its logo, during the render rather than in an effect
  // (CLAUDE.md §7): an effect would paint the old verdict once before correcting itself.
  const [broken, setBroken] = useState<string | null>(null);
  const [lastUrl, setLastUrl] = useState(logoUrl);
  if (lastUrl !== logoUrl) {
    setLastUrl(logoUrl);
    setBroken(null);
  }

  if (logoUrl && broken !== logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name ?? ""}
        className={className ?? "max-h-8 w-auto max-w-40 object-contain"}
        data-testid={testId ?? "portal-logo"}
        onError={() => setBroken(logoUrl)}
      />
    );
  }
  return (
    <span className="text-base font-semibold" data-testid={testId ?? "portal-roastery"}>
      {name ?? "—"}
    </span>
  );
}

/**
 * Paints the page in the roastery's colour and names the browser tab after it.
 *
 * <p>Rendered twice on purpose: once with what the address claims, before there is a token, and once
 * with what the token says, which is the true answer. The second call simply overwrites the first.
 */
export function BrandingTheme({ name, logoUrl, primaryColor }: RoasteryLook) {
  useEffect(() => {
    applyBranding({ name, logoUrl, primaryColor });
  }, [name, logoUrl, primaryColor]);
  return null;
}
